import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, type TShowSeries } from '@schemas'
import { reportActorKey, useCurrentUser, useShowSeriesDetail } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { PageHeading } from '@/components/page-heading'

export function useSeriesContext() {
  return useOutletContext<TShowSeries>()
}

function SeriesLayoutContent({ seriesId }: { seriesId: string }) {
  const detail = useShowSeriesDetail(seriesId)
  const qc = useQueryClient()
  const [denied, setDenied] = useState(false)
  const accessError =
    detail.error instanceof ApiError && [401, 403, 404].includes(detail.error.status)
  useEffect(() => {
    if (!accessError) return
    setDenied(true)
    void qc.cancelQueries({ queryKey: ['show-series'] })
    qc.removeQueries({ queryKey: ['show-series'] })
  }, [accessError, qc])
  if (denied || accessError)
    return (
      <div className="space-y-3">
        <p role="alert">This series is no longer available with your current access.</p>
        <Link className="underline" to="/show-series">
          Browse show series
        </Link>
      </div>
    )
  if (!detail.data)
    return detail.isError ? (
      <div role="alert">
        <p>Unable to load this series.</p>
        <Button onClick={() => void detail.refetch()}>Retry series</Button>
      </div>
    ) : (
      <p role="status">Loading series…</p>
    )
  return (
    <div className="space-y-6">
      <Link className="underline" to="/show-series">
        Browse show series
      </Link>
      <PageHeading>{detail.data.series.name}</PageHeading>
      {detail.isError && (
        <p role="alert">Unable to refresh series details. Please retry when connected.</p>
      )}
      <nav aria-label="Series sections" className="flex flex-wrap gap-4 border-b pb-3">
        {(
          [
            ['reports', 'Reports'],
            ['alignment', 'Match classes across years'],
            ['settings', 'Settings'],
          ] as const
        ).map(([path, label]) => (
          <NavLink
            key={path}
            to={`/show-series/${seriesId}/${path}`}
            className={({ isActive }) =>
              isActive
                ? 'font-semibold underline underline-offset-4'
                : 'text-muted-foreground hover:underline'
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>
      <Outlet context={detail.data.series} />
    </div>
  )
}

export default function SeriesLayout() {
  const { seriesId } = useParams<{ seriesId: string }>()
  const user = useCurrentUser()
  if (!user.data || !seriesId) return <p role="status">Checking series access…</p>
  return (
    <SeriesLayoutContent
      key={`${seriesId}:${JSON.stringify(reportActorKey(user.data))}`}
      seriesId={seriesId}
    />
  )
}
